import React, { useState } from "react";
import { Upload, Mail, Phone, User, Briefcase } from "lucide-react";
import emailjs from "@emailjs/browser";
import toast from "react-hot-toast";

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const CareerForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    message: "",
    resume: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "resume") {
      setFormData({ ...formData, resume: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          form_type: "Career Form",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: formData.position,
          message: formData.message || "—",
          time: new Date().toLocaleString(),
        },
        PUBLIC_KEY
      );

      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", position: "", message: "", resume: null });
    } catch (err) {
      console.error("Career form error:", err);
      const msg = "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-black text-white">
      <header className="text-center pt-20 pb-12">
        <h1 className="text-9xl font-bold font-modum text-brand">CAREER</h1>
      </header>
      <div className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-gray-300 mb-10 leading-relaxed">
            For now, no job availability — but you can submit your resume for future
            opportunities or apply for volunteering on this platform. Please fill and
            submit the form below.
          </p>
        </div>

        {submitted ? (
          <div className="max-w-3xl mx-auto text-center py-16">
            <p className="text-green-400 text-xl font-semibold">Thank you! Your application has been received.</p>
            <p className="text-gray-400 mt-2">We'll get back to you soon.</p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="max-w-3xl mx-auto bg-gray-800 p-8 rounded-2xl shadow-lg space-y-6"
          >
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

            {/* Name */}
            <div className="flex items-center border border-gray-700 rounded-md p-3 bg-gray-900">
              <User className="text-brand mr-3" />
              <input type="text" name="name" placeholder="Full Name" value={formData.name}
                onChange={handleChange} required
                className="w-full bg-transparent outline-none text-gray-200 placeholder-gray-400" />
            </div>

            {/* Email */}
            <div className="flex items-center border border-gray-700 rounded-md p-3 bg-gray-900">
              <Mail className="text-brand mr-3" />
              <input type="email" name="email" placeholder="Email Address" value={formData.email}
                onChange={handleChange} required
                className="w-full bg-transparent outline-none text-gray-200 placeholder-gray-400" />
            </div>

            {/* Phone */}
            <div className="flex items-center border border-gray-700 rounded-md p-3 bg-gray-900">
              <Phone className="text-brand mr-3" />
              <input type="text" name="phone" placeholder="Phone Number" value={formData.phone}
                onChange={handleChange} required
                className="w-full bg-transparent outline-none text-gray-200 placeholder-gray-400" />
            </div>

            {/* Position */}
            <div className="flex items-center border border-gray-700 rounded-md p-3 bg-gray-900">
              <Briefcase className="text-brand mr-3 shrink-0" />
              <select name="position" value={formData.position} onChange={handleChange} required
                className="w-full bg-transparent outline-none text-gray-200 appearance-none cursor-pointer">
                <option value="" disabled className="bg-gray-900">Position / Interest Area</option>
                <option value="Volunteer" className="bg-gray-900">Volunteer</option>
                <option value="Designer" className="bg-gray-900">Designer</option>
                <option value="Video Editor" className="bg-gray-900">Video Editor</option>
                <option value="Operations" className="bg-gray-900">Operations</option>
                <option value="Support" className="bg-gray-900">Support</option>
                <option value="Fund Raiser" className="bg-gray-900">Fund Raiser</option>
                <option value="Developer" className="bg-gray-900">Developer</option>
                <option value="AI Editor" className="bg-gray-900">AI Editor</option>
              </select>
            </div>

            {/* Resume Upload */}
            <div className="border border-gray-700 rounded-md p-4 bg-gray-900">
              <label className="flex items-center cursor-pointer space-x-3">
                <Upload className="text-brand" />
                <span className="text-sm text-gray-300">
                  {formData.resume ? formData.resume.name : "Upload Resume (PDF or DOC)"}
                </span>
                <input type="file" name="resume" accept=".pdf,.doc,.docx"
                  onChange={handleChange} className="hidden" />
              </label>
            </div>

            {/* Message */}
            <textarea name="message" rows="4" placeholder="Message (optional)"
              value={formData.message} onChange={handleChange}
              className="w-full border border-gray-700 bg-gray-900 rounded-md p-3 text-gray-200 placeholder-gray-400 outline-none focus:ring-2 focus:ring-brand" />

            {/* Submit */}
            <button type="submit" disabled={submitting}
              className="bg-brand text-gray-900 font-semibold px-8 py-3 rounded-md hover:bg-brand-dark transition-all duration-200 w-full disabled:opacity-50">
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default CareerForm;
